<template>
  <dropdown
    class="ifp-filter-tags__dropdown"
    placement="bottom"
    title="Tags"
  >
    <base-ifp-filter
      label="Tags"
      clearable
      @clear-clicked="handleClearClick()"
    >
      <template #value-display>
        <template v-if="hasSelectedTags">
          {{ numberSelectedTags }} selected
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div class="ifp-filter-tags__dropdown-tag-picker-wrapper">
        <div class="ifp-filter-tags__tab--wrapper">
          <div
            v-for="tab in tabs"
            :key="tab.name"
            :class="{ 'ifp-filter-tags__tab--active': tab.name === exposedTab}"
            class="ifp-filter-tags__tab"
            @click="exposeTab(tab.name)"
          >
            <span
              :class="{ 'ifp-filter-tags__tab-name--active': tab.name === exposedTab}"
              class="ifp-filter-tags__tab-name"
            >{{ tab.value }}</span>
            <span
              v-if="tab.name === 'included' && internalSelectedIncludingTagIDs.length > 0"
              class="ifp-filter-tags__tab-count"
            >{{ internalSelectedIncludingTagIDs.length }}</span>
            <span
              v-if="tab.name === 'excluded' && internalSelectedExcludingTagIDs.length > 0"
              class="ifp-filter-tags__tab-count"
            >{{ internalSelectedExcludingTagIDs.length }}</span>
          </div>
          <dd-button
            v-if="hasSelectedTags"
            type="text"
            color="blue"
            class="ifp-filter-tags__dropdown-tag-picker-clear"
            @click="clearAllTags"
          >Clear all</dd-button>
        </div>

        <!-- Selected tags for exposed tab -->

        <!-- Tag picker for "Included" Tags -->
        <tags-input
          v-if="isIncludedTabExposed"
          v-model:selected-tags="internalSelectedIncludingTagIDs"
          :available-tags="availableTagsToDisplay"
          search-only
          class="ifp-filter-tags__dropdown-tag-picker"
        />

        <!--Tag picker for "Excluded" Tags -->
        <tags-input
          v-if="isExcludedTabExposed"
          v-model:selected-tags="internalSelectedExcludingTagIDs"
          :available-tags="availableTagsToDisplay"
          search-only
          class="ifp-filter-tags__dropdown-tag-picker"
        />
      </div>
    </template>
  </dropdown>
 
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import isEqual from 'lodash-es/isEqual'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IItemForSelection, INameAndValue } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'IfpFilterTags',

  props: {
    selectedIncludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    selectedExcludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    tagOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
  },

  emits: [
    'selected-excluding-tag-ids-updated',
    'selected-including-tag-ids-updated',
  ],

  data() {
    return {
      exposedTab: 'included',
      /**
       * TABS
       */
      tabs: [
        {
          name: 'included',
          value: 'Include',
        },
        {
          name: 'excluded',
          value: 'Don’t Include',
        },
      ] as INameAndValue[],
    }
  },

  computed: {
    isIncludedTabExposed(): boolean {
      return this.exposedTab === 'included'
    },

    isExcludedTabExposed(): boolean {
      return this.exposedTab === 'excluded'
    },

    internalSelectedIncludingTagIDs: {
      get(): string[] {
        return this.tagOptions
          .filter(tag => this.selectedIncludingTagIds.includes(tag.id))
          .map(tag => tag.name)
      },
      set(newValue: string[]) {
        const newSelectedTagIds = this.tagOptions
          .filter(tag => newValue.includes(tag.name))
          .map(tag => tag.id)

        if (!isEqual(newSelectedTagIds, this.selectedIncludingTagIds)) {
          this.$emit('selected-including-tag-ids-updated', newSelectedTagIds)
        }
      },
    },

    internalSelectedExcludingTagIDs: {
      get(): string[] {
        return this.tagOptions
          .filter(tag => this.selectedExcludingTagIds.includes(tag.id))
          .map(tag => tag.name)
      },
      set(newValue: string[]) {
        const newSelectedTagIds = this.tagOptions
          .filter(tag => newValue.includes(tag.name))
          .map(tag => tag.id)

        if (!isEqual(newSelectedTagIds, this.selectedExcludingTagIds)) {
          this.$emit('selected-excluding-tag-ids-updated', newSelectedTagIds)
        }
      },
    },

    availableTagsToDisplay(): string[] {
      if (this.isIncludedTabExposed) {
        return this.tagOptions
          .filter(tag => !this.selectedExcludingTagIds.includes(tag.id))
          .map(tag => tag.name)
      } else {
        return this.tagOptions
          .filter(tag => !this.selectedIncludingTagIds.includes(tag.id))
          .map(tag => tag.name)
      }
    },

    numberSelectedTags(): number {
      return (
        this.internalSelectedIncludingTagIDs.length +
        this.internalSelectedExcludingTagIDs.length
      )
    },

    numberSelectedTagsDisplay(): string {
      return this.numberSelectedTags === 1
        ? this.numberSelectedTags + ' Tag'
        : this.numberSelectedTags + ' Tags'
    },

    hasSelectedTags(): boolean {
      return this.numberSelectedTags > 0
    },

    selectedTagsExposed(): string[] {
      if (this.isIncludedTabExposed) {
        return this.internalSelectedIncludingTagIDs
      } else {
        return this.internalSelectedExcludingTagIDs
      }
    },
  },

  methods: {
    removeTag(tag: string) {
      if (this.isIncludedTabExposed) {
        this.internalSelectedIncludingTagIDs = this.internalSelectedIncludingTagIDs.filter(
          t => t !== tag,
        )
      } else {
        this.internalSelectedExcludingTagIDs = this.internalSelectedExcludingTagIDs.filter(
          t => t !== tag,
        )
      }
    },

    clearAllTags() {
      this.$emit('selected-including-tag-ids-updated', [])
      this.$emit('selected-excluding-tag-ids-updated', [])
    },

    handleClearClick() {
      this.clearAllTags()
    },

    exposeTab(val: string) {
      this.exposedTab = val
    },
  },
})
</script>

<style lang="scss">
.ifp-filter-tags {
  &__dropdown {
    @include media-breakpoint-down(lg) {
      max-width: 150px;
    }

    .dropdown__trigger .dd-button {
      width: 100%;
    }

    .dropdown__popper {
      width: 375px;
      overflow: visible;
    }
  }

  &__dropdown-trigger {
    display: block;
    width: 100%;
    height: 36px;
    padding: 0 12px;
    background: $color--white;

    .dd-button__content {
      span {
        padding-top: 1px;
        @include lettering--p-sm;
        @include single-line-ellipsis;
      }

      i {
        @include dropdown-caret-size-color;
      }
    }
  }

  &__dropdown-tag-picker {
    .el-select__input {
      /* Forces this input to always break to a second line so it
      goes under the selected tags */
      min-width: 288px !important;
    }

    &-wrapper {
      /* Additional side-padding around normal 8px dropdown padding */
      padding: 0 4px;
    }

    &-clear {
      height: auto;
      padding: 0;
      padding-bottom: 6px;
      margin-left: auto;
      font-size: 13px;
    }

    &-empty {
      padding: 16px 0;
      font-size: 13px;
      line-height: 20px;
      color: var(--color-text-third);
      text-align: center;
    }
  }

  &__tab {
    position: relative;
    display: inline-block;
    padding-bottom: 6px;
    margin-right: 24px;
    cursor: pointer;

    &-name {
      @include lettering--p-sm;
      color: var(--color-text-third);
      font-weight: $bold;

      &--active {
        color: var(--color-text-secondary);
      }
    }

    &-count {
      margin-left: 4px;
      @include lettering--p-sm;
      color: var(--color-text-third);
    }

    &--wrapper {
      display: flex;
      border-bottom: 1px solid $color--gray-30;
      margin-bottom: 4px;
    }

    &--active {
      &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        content: '';
        background-color: $color--blue;
        border-radius: 1.5px;
      }
    }
  }
}
</style>
